import {
	BackIcon,
	cn,
	IconButton,
	MagnifyingGlassIcon,
} from "@au-schein-de/au-ui-library";
import {
	DELIVERY_METHOD_KIND,
	type DeliveryMethodKind,
} from "@dransay/api/models";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router";
import { DeliveryMethodSelectorMobilePanel } from "~/components/delivery-method-selector/delivery-method-selector-mobile-panel.component";
import { Logo } from "~/components/logo.component";
import { useVendorSelection } from "~/contexts/vendor-selection.context";
import { FULFILLMENT_GROUP_KIND } from "~/features/checkout/delivery/fulfillment-groups.resolver";
import type { DeliveryAddressCookieData } from "~/features/delivery-address/delivery-address.cookie";
import { ExpressAvailabilityHeaderCardHost } from "~/features/products/express-availability-header-card-host.component";
import { SearchBar } from "~/features/products/product-list.search-bar.component";
import { useDisclosure } from "~/hooks/use-disclosure";
import type { SessionUser } from "~/tooling/session";
import { captureEvent } from "~/tooling/telemetry/capture-event";
import {
	deliveryMethodSelectedEvent,
	navigationClickedEvent,
} from "~/tooling/telemetry/events";
import { HeaderFavoritesButton } from "../auth/auth.header-favorites-button.component";
import { UserProfileButton } from "../auth/auth.user-profile-button.component";
import { Cart } from "../cart/cart.component";
import { useCart } from "../cart/cart.context";
import { buildProductsHref } from "./build-products-href";

const HIDDEN_HEADER_METHODS: ReadonlyArray<DeliveryMethodKind> = [
	DELIVERY_METHOD_KIND.EXPRESS_COURIER,
];

interface StorefrontHeaderMobileProps {
	logoVariant: "default" | "navigation";
	user?: SessionUser;
	deliveryAddress?: DeliveryAddressCookieData | null;
	onOpenAddressModal?: () => void;
	expressPilotVendorIds?: readonly string[];
	enablePlpExpressAvailabilityModal?: boolean;
}

export function StorefrontHeaderMobile({
	logoVariant,
	user,
	deliveryAddress,
	onOpenAddressModal,
	expressPilotVendorIds = [],
	enablePlpExpressAvailabilityModal = false,
}: StorefrontHeaderMobileProps) {
	const [searchParams] = useSearchParams();
	const { t } = useTranslation("common");
	const searchDisclosure = useDisclosure(false);
	const deliveryPanelDisclosure = useDisclosure(false);
	const { pathname } = useLocation();
	const hasSearchQuery = searchParams.has("search");
	const closeSearch = searchDisclosure.close;
	const previousPathname = useRef(pathname);

	// Typing on a non-listing page moves to /products?search=…, so only a page
	// change that drops the search query (e.g. opening a result) closes search.
	useEffect(() => {
		if (previousPathname.current === pathname) return;
		previousPathname.current = pathname;
		if (!hasSearchQuery) closeSearch();
	}, [pathname, hasSearchQuery, closeSearch]);

	const {
		selectedVendorId,
		selectedDeliveryMethodKind,
		setSelectedDeliveryMethodKind,
	} = useVendorSelection();
	const { cartDisclosure } = useCart();

	const productsHref = buildProductsHref({
		searchParams,
		vendorId: selectedVendorId,
		deliveryMethod: selectedDeliveryMethodKind,
	});

	const handleSearchToggle = useCallback(
		(elementId: string) => {
			captureEvent(navigationClickedEvent(elementId, "", "main"));
			if (!searchDisclosure.isOpen && deliveryPanelDisclosure.isOpen) {
				deliveryPanelDisclosure.close();
			}
			searchDisclosure.toggle();
		},
		[deliveryPanelDisclosure, searchDisclosure],
	);

	const handleDeliveryPanelOpenChange = useCallback(
		(nextOpen: boolean) => {
			if (nextOpen) {
				searchDisclosure.close();
				deliveryPanelDisclosure.open();
				return;
			}

			deliveryPanelDisclosure.close();
		},
		[deliveryPanelDisclosure, searchDisclosure],
	);

	const handleMethodSelect = useCallback(
		async (method: DeliveryMethodKind, closePanel: () => void) => {
			captureEvent(
				deliveryMethodSelectedEvent(method, FULFILLMENT_GROUP_KIND.MEDICATION),
			);
			try {
				await setSelectedDeliveryMethodKind(method);
			} finally {
				closePanel();
			}
		},
		[setSelectedDeliveryMethodKind],
	);

	return (
		<header
			data-testid="storefront-header-mobile-shell"
			className={cn(
				"flex md:hidden sticky top-0 w-full h-(--storefront-header-height-mobile) flex-col gap-2 bg-ansay-900 p-4 backdrop-blur-[12px] z-(--z-index-cart-dialog-content)",
				{
					"h-auto": searchDisclosure.isOpen,
					"!hidden": cartDisclosure.isOpen,
				},
			)}
		>
			<div className="relative z-50 flex w-full items-center justify-between">
				<a
					id="header-logo-link-mobile"
					href={productsHref}
					aria-label={t("header.logo.label", "Go to products")}
					className="flex h-[26px] w-[82px] shrink-0 items-center"
					onClick={(e) => {
						const elementId = e.currentTarget.id;
						captureEvent(navigationClickedEvent(elementId, "", "main"));
					}}
				>
					<Logo size={26} variant="white" />
					{logoVariant === "navigation" && (
						<span className="pb-2 text-caption-emphasis text-white">UK</span>
					)}
				</a>

				<div
					data-testid="mobile-header-actions"
					className="flex items-center gap-2 [&_button]:!w-[40px] [&_button]:!h-[40px] [&_button]:!min-w-[40px] [&_button]:!min-h-[40px] [&_button]:!rounded-[12px] [&_button]:!gap-[8px] [&_button>span>span]:!w-[20px] [&_button>span>span]:!h-[20px] [&_button>span>span]:!min-w-[20px] [&_button>span>span]:!min-h-[20px] [&_button>span>span_svg]:!w-[20px] [&_button>span>span_svg]:!h-[20px]"
				>
					{!cartDisclosure.isOpen ? (
						<>
							<IconButton
								id="header-toggle-search-mobile"
								aria-label={t("header.search.toggle", "Search")}
								icon={<MagnifyingGlassIcon.Raw />}
								shape="square"
								onClick={(e) => handleSearchToggle(e.currentTarget.id)}
								data-testid="mobile-search-toggle"
							/>
							<HeaderFavoritesButton />
							<UserProfileButton user={user} renderModals={false} />
						</>
					) : null}
					<Cart mobileIconOnly renderDialog={false} />
				</div>
			</div>

			{searchDisclosure.isOpen ? (
				<div data-testid="mobile-search-expanded" className="w-full">
					<SearchBar
						autoFocus
						leftIcon={
							<IconButton
								size="sm"
								variant="input"
								aria-label={t("header.search.close", "Close search")}
								icon={<BackIcon size={6} colorToken="gray-500" />}
								shape="square"
								onClick={searchDisclosure.close}
							/>
						}
					/>
				</div>
			) : (
				<DeliveryMethodSelectorMobilePanel
					isOpen={deliveryPanelDisclosure.isOpen}
					onOpenChange={handleDeliveryPanelOpenChange}
					selectedMethod={selectedDeliveryMethodKind ?? "shipping"}
					onMethodSelect={handleMethodSelect}
					deliveryMethodLabel={t("header.deliveryMethod.label")}
					getMethodLabel={(method) => t(`header.deliveryMethod.${method}`)}
					addressLabel={t("header.address.label")}
					addressText={
						deliveryAddress?.displayString ?? t("header.address.placeholder")
					}
					isAddressPlaceholder={!deliveryAddress}
					addressActionLabel={
						deliveryAddress
							? t("header.address.change")
							: t("header.address.apply")
					}
					onAddressAction={onOpenAddressModal}
					hiddenMethods={HIDDEN_HEADER_METHODS}
					expressPromoSlot={
						<ExpressAvailabilityHeaderCardHost
							enabled={
								enablePlpExpressAvailabilityModal &&
								(!user || user.role === "customer")
							}
							postalCode={deliveryAddress?.postalCode}
							expressVendorIds={expressPilotVendorIds}
							selectedDeliveryMethodKind={
								selectedDeliveryMethodKind ?? "shipping"
							}
						/>
					}
				/>
			)}
		</header>
	);
}
